import { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useRoomStore } from '@/lib/store/room-store'
import { useUIStore } from '@/lib/store/ui-store'
import { useTimelineStore } from '@/lib/store/timeline-store'
import { Timeline } from '@/components/timeline/Timeline'
import { TypingIndicator } from '@/components/timeline/TypingIndicator'
import { MessageComposer } from '@/components/composer/MessageComposer'
import { MemberListPanel } from '@/components/members/MemberListPanel'
import { ThreadPanel } from '@/components/timeline/ThreadPanel'
import { HermesPanel } from '@/components/timeline/HermesPanel'
import { EncryptionBadge } from '@/components/encryption/EncryptionBadge'
import { Avatar } from '@/components/ui/Avatar'
import { BridgeBadge } from '@/components/ui/BridgeBadge'
import type { BridgeType } from '@/lib/store/room-store'
import { requireClient } from '@/lib/matrix/client'
import { toggleRoomDirect } from '@/lib/matrix/dm'
import { markRoomAsRead } from '@/lib/matrix/receipts'
import { EmojiPicker } from '@/components/composer/EmojiPicker'
import { RoomSettingsDialog } from './RoomSettingsDialog'
import type { SerializedEvent } from '@/lib/matrix/events'

export function RoomView() {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const room = useRoomStore((s) => (roomId ? s.rooms.get(roomId) : undefined))
  const upsertRoom = useRoomStore((s) => s.upsertRoom)
  const { memberPanelOpen, setMemberPanelOpen, toggleAiForRoom, toggleMuteRoom } = useUIStore()
  const isMuted = useUIStore((s) => roomId ? s.mutedRooms.has(roomId) : false)
  const aiEnabled = useUIStore((s) => roomId ? s.aiEnabledRooms.has(roomId) : false)
  const [replyTo, setReplyTo] = useState<SerializedEvent | null>(null)
  const [editingEvent, setEditingEvent] = useState<SerializedEvent | null>(null)
  const [reactingTo, setReactingTo] = useState<SerializedEvent | null>(null)
  const [threadRoot, setThreadRoot] = useState<SerializedEvent | null>(null)
  const [droppedFiles, setDroppedFiles] = useState<File[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [hermesOpen, setHermesOpen] = useState(false)

  useEffect(() => {
    if (!roomId) return
    markRoomAsRead(roomId)
    upsertRoom(roomId, { unreadCount: 0, highlightCount: 0, hasUnread: false })
    setThreadRoot(null)
  }, [roomId, upsertRoom])

  const handleReply = useCallback((event: SerializedEvent) => {
    setReplyTo(event)
    setEditingEvent(null)
  }, [])

  const handleEdit = useCallback((event: SerializedEvent) => {
    setEditingEvent(event)
    setReplyTo(null)
  }, [])

  // Slack-style ↑: find your most recent editable message and open it for editing,
  // prefilled with its current text (latest edit wins).
  const handleEditLast = useCallback(() => {
    if (!roomId) return
    const events = useTimelineStore.getState().timelines.get(roomId) ?? []
    const me = requireClient().getUserId()

    // Latest edit per original event, so the composer prefills with current text.
    const latestEdit = new Map<string, SerializedEvent>()
    for (const e of events) {
      const rel = e.content['m.relates_to'] as Record<string, unknown> | undefined
      if (rel?.rel_type === 'm.replace' && typeof rel.event_id === 'string') {
        const cur = latestEdit.get(rel.event_id)
        if (!cur || e.timestamp > cur.timestamp) latestEdit.set(rel.event_id, e)
      }
    }

    const EDITABLE = new Set(['m.text', 'm.emote', 'm.notice'])
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]!
      if (e.sender !== me || e.type !== 'm.room.message') continue
      const rel = e.content['m.relates_to'] as Record<string, unknown> | undefined
      // Skip edit events and thread replies; only edit plain text-ish messages.
      if (rel?.rel_type === 'm.replace' || rel?.rel_type === 'm.thread') continue
      if (!EDITABLE.has(e.content.msgtype as string)) continue
      const newContent = latestEdit.get(e.eventId)?.content['m.new_content'] as
        | Record<string, unknown>
        | undefined
      handleEdit(newContent ? { ...e, content: newContent } : e)
      return
    }
  }, [roomId, handleEdit])

  const handleReact = useCallback((event: SerializedEvent) => {
    setReactingTo(event)
  }, [])

  const handleEmojiReaction = useCallback(async (emoji: string) => {
    if (!reactingTo || !roomId) return
    const client = requireClient()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (client as any).sendEvent(roomId, 'm.reaction', {
      'm.relates_to': {
        rel_type: 'm.annotation',
        event_id: reactingTo.eventId,
        key: emoji,
      },
    })
    setReactingTo(null)
  }, [reactingTo, roomId])

  if (!roomId || !room) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-[rgb(var(--np-tx-muted))] text-sm">Room not found</p>
      </div>
    )
  }

  const memberCount = requireClient().getRoom(roomId)?.getJoinedMemberCount() ?? 0

  return (
    <div className="flex-1 flex min-h-0">
      <div
        className={`flex-1 flex flex-col min-w-0 min-h-0 relative ${dragOver ? 'ring-2 ring-inset ring-[rgb(var(--np-accent))]' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          const files = Array.from(e.dataTransfer.files)
          if (files.length > 0) setDroppedFiles(files)
        }}
      >
        <header className="chat-header">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {room.isDirect ? (
              <Avatar mxc={room.avatarUrl} name={room.name} size={38} square />
            ) : (
              <span className="hash-tile">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" />
                  <line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" />
                </svg>
              </span>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => setShowSettings(true)}
                  className="text-[rgb(var(--np-tx-primary))] truncate hover:underline cursor-pointer"
                  style={{ fontSize: 15, fontWeight: 700 }}
                >
                  {room.name}
                </button>
                <EncryptionBadge encrypted={room.isEncrypted} />
                {room.bridgeType && (
                  <span className="origin-chip">
                    <BridgeBadge type={room.bridgeType} size={11} />
                    {BRIDGE_LABELS[room.bridgeType]}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-[rgb(var(--np-status-ok))] shrink-0" />
                <span className="text-[11.5px] text-[rgb(var(--np-tx-muted))]">
                  <span className="font-data text-[rgb(var(--np-tx-secondary))]">{memberCount}</span> members
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              onClick={() => { setHermesOpen((o) => !o); setMemberPanelOpen(false) }}
              className={`icon-btn${hermesOpen ? ' icon-btn-on' : ''}`}
              title="Ask Hermes"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
                <path d="M19 15l.7 1.9L21.5 17.5l-1.8.6L19 20l-.7-1.9L16.5 17.5l1.8-.6z" />
              </svg>
            </button>
            <button
              onClick={() => { setMemberPanelOpen(!memberPanelOpen); setHermesOpen(false) }}
              className={`icon-btn${memberPanelOpen ? ' icon-btn-on' : ''}`}
              title="Members"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </button>
            <RoomMoreMenu
              isMuted={isMuted}
              aiEnabled={aiEnabled}
              isDirect={room.isDirect}
              onMute={() => toggleMuteRoom(roomId)}
              onAi={() => toggleAiForRoom(roomId)}
              onMarkDirect={() => toggleRoomDirect(requireClient(), roomId)}
              onSettings={() => setShowSettings(true)}
              onLeave={async () => {
                if (!confirm(`Leave "${room.name}"?`)) return
                await requireClient().leave(roomId)
                navigate('/')
              }}
            />
          </div>
        </header>
        {room.topic && <TopicBar topic={room.topic} />}

        <Timeline
          roomId={roomId}
          aiEnabled={aiEnabled}
          onReply={handleReply}
          onEdit={handleEdit}
          onReact={handleReact}
          onOpenThread={setThreadRoot}
        />

        <TypingIndicator roomId={roomId} />

        {reactingTo && (
          <div className="relative">
            <EmojiPicker
              onSelect={handleEmojiReaction}
              onClose={() => setReactingTo(null)}
            />
          </div>
        )}

        <MessageComposer
          primary
          roomId={roomId}
          replyTo={replyTo}
          editingEvent={editingEvent}
          droppedFiles={droppedFiles}
          onCancelReply={() => setReplyTo(null)}
          onCancelEdit={() => setEditingEvent(null)}
          onEditLast={handleEditLast}
          onDroppedFilesConsumed={() => setDroppedFiles([])}
        />
        {showSettings && (
          <RoomSettingsDialog roomId={roomId} onClose={() => setShowSettings(false)} />
        )}
      </div>

      {threadRoot ? (
        <ThreadPanel
          roomId={roomId}
          rootEvent={threadRoot}
          onClose={() => setThreadRoot(null)}
        />
      ) : hermesOpen ? (
        <HermesPanel roomId={roomId} roomName={room.name} onClose={() => setHermesOpen(false)} />
      ) : memberPanelOpen ? (
        <MemberListPanel
          roomId={roomId}
          onClose={() => setMemberPanelOpen(false)}
        />
      ) : null}
    </div>
  )
}

const BRIDGE_LABELS: Record<NonNullable<BridgeType>, string> = {
  whatsapp: 'WhatsApp', telegram: 'Telegram', signal: 'Signal', discord: 'Discord', slack: 'Slack',
}

// Collapsed one-line topic with expand-on-demand — fixes the runaway full-width topic header.
function TopicBar({ topic }: { topic: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="topic-bar">
      <button className="topic-toggle" onClick={() => setOpen((o) => !o)}>
        <svg className="shrink-0 text-[rgb(var(--np-tx-muted))] mt-px" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
        </svg>
        <span className="topic-text" style={open ? { whiteSpace: 'normal' } : undefined}>{topic}</span>
        <span className="topic-chev" style={{ transform: open ? 'rotate(180deg)' : 'none' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </span>
      </button>
    </div>
  )
}

interface RoomMoreMenuProps {
  isMuted: boolean
  aiEnabled: boolean
  isDirect: boolean
  onMute: () => void
  onAi: () => void
  onMarkDirect: () => void
  onSettings: () => void
  onLeave: () => void
}

// Less-frequent room actions consolidated into an overflow menu.
function RoomMoreMenu({ isMuted, aiEnabled, isDirect, onMute, onAi, onMarkDirect, onSettings, onLeave }: RoomMoreMenuProps) {
  const [open, setOpen] = useState(false)
  const run = (fn: () => void) => () => { setOpen(false); fn() }
  return (
    <div className="relative">
      <button className={`icon-btn${open ? ' icon-btn-on' : ''}`} title="More" onClick={() => setOpen((o) => !o)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 w-52 py-1 bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg shadow-lg">
            <MenuItem label={isMuted ? 'Unmute notifications' : 'Mute notifications'} active={isMuted} onClick={run(onMute)} />
            <MenuItem label={aiEnabled ? 'AI detection: on' : 'AI detection: off'} active={aiEnabled} onClick={run(onAi)} />
            <MenuItem label={isDirect ? 'Move to Rooms' : 'Mark as direct'} active={isDirect} onClick={run(onMarkDirect)} />
            <MenuItem label="Room settings" onClick={run(onSettings)} />
            <div className="h-px my-1 bg-[rgb(var(--np-border))]" />
            <MenuItem label="Leave room" danger onClick={run(onLeave)} />
          </div>
        </>
      )}
    </div>
  )
}

function MenuItem({ label, onClick, active, danger }: { label: string; onClick: () => void; active?: boolean; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-1.5 text-sm transition-colors hover:bg-[rgb(var(--np-raised))] ${
        danger ? 'text-[rgb(var(--np-status-critical))]' : active ? 'text-[rgb(var(--np-accent))]' : 'text-[rgb(var(--np-tx-secondary))]'
      }`}
    >
      {label}
    </button>
  )
}
